import { useEffect } from 'react';
import { analytics } from '../../../firebase';
import { logEvent } from 'firebase/analytics';

export default function TeamLinkPrivacy() {
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Privacy Policy - TeamLink',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    window.scrollTo(0, 0);
  }, []);

  const primaryColor = '#2563eb';
  const backgroundColor = '#f8f9fa';
  const darkTextColor = '#333333';
  const lightTextColor = '#666666';
  const borderColor = '#e9ecef';

  const sectionHeaderStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${borderColor}`,
    marginTop: '32px'
  };

  const subHeaderStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '12px',
    marginTop: '24px'
  };

  const cardStyle = {
    padding: '24px',
    backgroundColor: backgroundColor,
    borderRadius: '8px',
    border: `1px solid ${borderColor}`,
    marginBottom: '32px'
  };

  const headingStyle = {
    fontWeight: '300',
    color: '#1a1a1a'
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#ffffff',
      color: darkTextColor,
      lineHeight: '1.6',
      fontSize: '16px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: backgroundColor,
        padding: '40px 20px',
        textAlign: 'center',
        borderBottom: `1px solid ${borderColor}`
      }}>
        <img
          src="/apps/teamlink/logo.png"
          alt="TeamLink Logo"
          style={{
            width: '64px',
            height: '64px',
            marginBottom: '20px',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        />
        <h1 style={{ ...headingStyle, fontSize: '2.5rem', margin: '0 0 8px 0' }}>
          Privacy Policy
        </h1>
        <p style={{
          fontSize: '18px',
          color: primaryColor,
          margin: '0 0 8px 0',
          fontWeight: '500'
        }}>
          TeamLink - Sync, Share and Search Bookmarks
        </p>
        <p style={{
          fontSize: '14px',
          color: lightTextColor,
          margin: '0'
        }}>
          Last updated: September 28, 2021
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        
        <section>
          <p>This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.</p>
          <p>We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.</p>
        </section>

        <section id="interpretation-and-definitions">
          <h2 style={sectionHeaderStyle}>INTERPRETATION AND DEFINITIONS</h2>
          <h3 style={subHeaderStyle}>INTERPRETATION</h3>
          <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>
          
          <h3 style={subHeaderStyle}>DEFINITIONS</h3>
          <p>For the purposes of this Privacy Policy:</p>
          <ul style={{ paddingLeft: '20px' }}>
            <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
            <li><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
            <li><strong>Application</strong> means the software program provided by the Company downloaded by You on any electronic device, named Teamlink Browser extensions & Mobile apps.</li>
            <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Teamlink.</li>
            <li><strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
            <li><strong>Country</strong> refers to: Maharashtra, India.</li>
            <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
            <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
            <li><strong>Service</strong> refers to the Application or the Website or both.</li>
            <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company.</li>
            <li><strong>Third-party Social Media Service</strong> refers to any website or any social network website through which a User can log in or create an account to use the Service.</li>
            <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</li>
            <li><strong>Website</strong> refers to Teamlink, accessible from https://www.getteamlink.web.app.</li>
            <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
          </ul>
        </section>

        <section id="collecting-and-using-your-personal-data">
          <h2 style={sectionHeaderStyle}>COLLECTING AND USING YOUR PERSONAL DATA</h2>
          <h3 style={subHeaderStyle}>TYPES OF DATA COLLECTED</h3>
          
          <div style={cardStyle}>
            <h4 style={{ marginTop: 0 }}>PERSONAL DATA</h4>
            <p>While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:</p>
            <ul>
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Usage Data</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginTop: 0 }}>USAGE DATA</h4>
            <p>Usage Data is collected automatically when using the Service. It may include information such as Your Device's IP address, browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, and other diagnostic data.</p>
          </div>

          <h3 style={subHeaderStyle}>INFORMATION FROM THIRD-PARTY SOCIAL MEDIA SERVICES</h3>
          <p>The Company allows You to create an account and log in to use the Service through Google, Facebook, and Twitter. If You decide to register through or otherwise grant us access to a Third-Party Social Media Service, We may collect Personal data that is already associated with Your account.</p>

          <h3 style={subHeaderStyle}>TRACKING TECHNOLOGIES AND COOKIES</h3>
          <p>We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service.</p>
        </section>

        <section id="use-of-your-personal-data">
          <h2 style={sectionHeaderStyle}>USE OF YOUR PERSONAL DATA</h2>
          <p>The Company may use Personal Data for the following purposes:</p>
          <ul style={{ paddingLeft: '20px' }}>
            <li>To provide and maintain our Service.</li>
            <li>To manage Your Account.</li>
            <li>For the performance of a contract.</li>
            <li>To contact You.</li>
            <li>To provide You with news, special offers and general information.</li>
            <li>To manage Your requests.</li>
            <li>For business transfers.</li>
            <li>For other purposes like data analysis and improving our Service.</li>
          </ul>
        </section>

        <section id="retention-of-your-personal-data">
          <h2 style={sectionHeaderStyle}>RETENTION OF YOUR PERSONAL DATA</h2>
          <p>The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.</p>
          <p>The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.</p>
        </section>

        <section id="transfer-of-your-personal-data">
          <h2 style={sectionHeaderStyle}>TRANSFER OF YOUR PERSONAL DATA</h2>
          <p>Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.</p>
          <p>Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.</p>
        </section>

        <section id="disclosure-of-your-personal-data">
          <h2 style={sectionHeaderStyle}>DISCLOSURE OF YOUR PERSONAL DATA</h2>
          <h3 style={subHeaderStyle}>Business Transactions</h3>
          <p>If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.</p>
          <h3 style={subHeaderStyle}>Law Enforcement</h3>
          <p>Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).</p>
          <h3 style={subHeaderStyle}>Other Legal Requirements</h3>
          <p>The Company may disclose Your Personal Data in the good faith belief that such action is necessary to: comply with a legal obligation, protect and defend the rights or property of the Company, prevent or investigate possible wrongdoing in connection with the Service, protect the personal safety of Users of the Service or the public, or protect against legal liability.</p>
        </section>

        <section id="security-of-your-personal-data">
          <h2 style={sectionHeaderStyle}>SECURITY OF YOUR PERSONAL DATA</h2>
          <p>The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.</p>
        </section>

        <section id="childrens-privacy">
          <h2 style={sectionHeaderStyle}>CHILDREN'S PRIVACY</h2>
          <p>Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.</p>
        </section>

        <section id="links-to-other-websites">
          <h2 style={sectionHeaderStyle}>LINKS TO OTHER WEBSITES</h2>
          <p>Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.</p>
          <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.</p>
        </section>

        <section id="changes-to-this-privacy-policy">
          <h2 style={sectionHeaderStyle}>CHANGES TO THIS PRIVACY POLICY</h2>
          <p>We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.</p>
          <p>We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.</p>
          <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
        </section>

        <section id="contact-us" style={{ marginBottom: '32px' }}>
          <h2 style={sectionHeaderStyle}>CONTACT US</h2>
          <p>If you have any questions about this Privacy Policy, You can contact us:</p>
          <div style={cardStyle}>
            <strong>By visiting this page on our website:</strong> <a href="http://www.getteamlink.web.app/contact.html" style={{ color: primaryColor }}>http://www.getteamlink.web.app/contact.html</a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '24px 20px',
        backgroundColor: backgroundColor,
        borderTop: `1px solid ${borderColor}`,
        color: lightTextColor,
        fontSize: '14px'
      }}>
        <p style={{ margin: '0' }}>
          © 2026 Tattva Dynamics. All rights reserved.
        </p>
      </div>
    </div>
  );
}
